import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Check, LifeBuoy, Plus, X } from 'lucide-react';

import { HealthSyncCard } from '@/components/tracker/HealthSyncCard';
import { PulseScannerDialog } from '@/components/tracker/PulseScannerDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import {
  FLOW_LABELS,
  SYMPTOM_OPTIONS,
  formatLong,
  painLabel,
  type DayLog,
  type FlowLevel,
} from '@/lib/cycle';

interface QuickLogFormProps {
  /** ISO date being logged. */
  date: string;
  existing: DayLog | undefined;
  onSave: (log: DayLog) => void;
  /** User-added symptoms, shown as extra chips. */
  customSymptoms: string[];
  onAddSymptom: (name: string) => void;
  onRemoveSymptom: (name: string) => void;
  /** Gentle mode: default pain to 0 for people without flare-ups. */
  gentle?: boolean;
  /** Which dimensions to show (from cycle settings). Hidden fields keep
   *  any previously logged values when saving. */
  showPain?: boolean;
  showFlow?: boolean;
  showVitals?: boolean;
  /** Saved plan text for this day, and a saver for it. */
  event?: string;
  onSaveEvent?: (text: string) => void;
}

const FLOW_ORDER: FlowLevel[] = ['none', 'light', 'medium', 'heavy'];

function flowButtonClass(flow: FlowLevel, active: boolean): string {
  if (!active) return 'border-border bg-white text-muted-foreground hover:bg-lavender/40';
  switch (flow) {
    case 'none':
      return 'border-lavender-strong/50 bg-lavender text-lavender-foreground';
    case 'light':
      return 'border-blush-strong/40 bg-blush/70 text-blush-foreground';
    case 'medium':
      return 'border-blush-strong/60 bg-blush text-blush-foreground';
    case 'heavy':
      return 'border-blush-strong bg-blush-strong text-white';
  }
}

export function QuickLogForm({
  date,
  existing,
  onSave,
  customSymptoms = [],
  onAddSymptom,
  onRemoveSymptom,
  gentle = false,
  showPain = true,
  showFlow = true,
  showVitals = true,
  event = '',
  onSaveEvent,
}: QuickLogFormProps) {
  const [pain, setPain] = useState(existing?.pain ?? (gentle ? 0 : 3));
  const [flow, setFlow] = useState<FlowLevel>(existing?.flow ?? 'none');
  const [heartRate, setHeartRate] = useState(existing?.heartRate ? String(existing.heartRate) : '');
  const [hrv, setHrv] = useState(existing?.hrv ? String(existing.hrv) : '');
  const [symptoms, setSymptoms] = useState<Set<string>>(new Set(existing?.symptoms ?? []));
  const [syncedLabel, setSyncedLabel] = useState<string | null>(null);
  const [addingSymptom, setAddingSymptom] = useState(false);
  const [newSymptom, setNewSymptom] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [eventText, setEventText] = useState(event);

  // Reset the form whenever a different day is selected.
  useEffect(() => {
    setPain(existing?.pain ?? (gentle ? 0 : 3));
    setFlow(existing?.flow ?? 'none');
    setHeartRate(existing?.heartRate ? String(existing.heartRate) : '');
    setHrv(existing?.hrv ? String(existing.hrv) : '');
    setSymptoms(new Set(existing?.symptoms ?? []));
    setSyncedLabel(null);
    setAddingSymptom(false);
    setNewSymptom('');
    setEventText(event);
  }, [date, existing, gentle, event]);

  const allSymptoms: string[] = [...SYMPTOM_OPTIONS, ...customSymptoms];

  const toggleSymptom = (symptom: string) => {
    setSymptoms((prev) => {
      const next = new Set(prev);
      if (next.has(symptom)) next.delete(symptom);
      else next.add(symptom);
      return next;
    });
  };

  const submitNewSymptom = () => {
    const trimmed = newSymptom.trim();
    if (trimmed.length < 2) return;
    onAddSymptom(trimmed);
    setSymptoms((prev) => new Set(prev).add(trimmed));
    setNewSymptom('');
    setAddingSymptom(false);
  };

  const handleSave = () => {
    const hrNum = heartRate.trim() === '' ? undefined : Math.round(Number(heartRate));
    const hrvNum = hrv.trim() === '' ? undefined : Math.round(Number(hrv));
    onSaveEvent?.(eventText);
    onSave({
      date,
      // Hidden dimensions preserve whatever was logged before.
      pain: showPain ? pain : (existing?.pain ?? 0),
      flow: showFlow ? flow : (existing?.flow ?? 'none'),
      heartRate: showVitals ? (Number.isFinite(hrNum) ? hrNum : undefined) : existing?.heartRate,
      hrv: showVitals ? (Number.isFinite(hrvNum) ? hrvNum : undefined) : existing?.hrv,
      symptoms: allSymptoms.filter((s) => symptoms.has(s)),
    });
  };

  return (
    <>
    <Card className="rounded-3xl shadow-sm">
      <CardHeader>
        <CardTitle className="text-xl font-bold">
          {existing ? 'Update log' : 'Quick log'} · {formatLong(date)}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-7">
        {/* Pain severity */}
        {showPain && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <label htmlFor="pain-slider" className="text-base font-semibold">
              Pain severity
            </label>
            <span
              className={cn(
                'rounded-full px-3.5 py-1 text-sm font-bold',
                pain >= 7
                  ? 'bg-blush-strong text-white'
                  : pain >= 4
                    ? 'bg-lavender text-lavender-foreground'
                    : 'bg-aqua text-aqua-foreground',
              )}
            >
              {pain}/10 · {painLabel(pain)}
            </span>
          </div>
          <Slider
            id="pain-slider"
            min={0}
            max={10}
            step={1}
            value={[pain]}
            onValueChange={([v]) => setPain(v ?? 0)}
            aria-label="Pain severity from 0 to 10"
            className="py-3 [&_[data-slot=slider-thumb]]:size-7 [&_[data-slot=slider-track]]:h-2.5"
          />
          <div className="flex justify-between text-xs font-medium text-muted-foreground">
            <span>No pain</span>
            <span>Worst</span>
          </div>

          {/* Live guidance as the slider moves into higher pain */}
          {pain >= 7 ? (
            <div className="space-y-2 rounded-2xl border border-blush-strong/40 bg-blush/50 p-4">
              <p className="text-sm font-bold text-blush-foreground">Severe pain today</p>
              <p className="text-sm leading-relaxed text-foreground/80">
                If you can, reschedule demanding tasks and keep today low-key. Warmth, rest,
                hydration, and slow breathing can take the edge off.
              </p>
              <Link
                to="/care?sos=1"
                className="inline-flex items-center gap-2 rounded-xl bg-blush-strong px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-blush-strong/90"
              >
                <LifeBuoy className="size-4" aria-hidden />
                Open the instant SOS guide
              </Link>
            </div>
          ) : pain >= 4 ? (
            <div className="rounded-2xl border border-lavender-strong/40 bg-lavender/50 p-4">
              <p className="text-sm leading-relaxed text-lavender-foreground">
                <strong>Moderate pain</strong> — consider lighter tasks today and plan rest
                breaks where you can.
              </p>
            </div>
          ) : null}
        </div>
        )}

        {/* Flow */}
        {showFlow && (
        <div className="space-y-3">
          <span className="text-base font-semibold">Flow</span>
          <div className="grid grid-cols-4 gap-2">
            {FLOW_ORDER.map((level) => (
              <button
                key={level}
                type="button"
                onClick={() => setFlow(level)}
                aria-pressed={flow === level}
                className={cn(
                  'h-12 rounded-xl border-2 text-sm font-bold transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  flowButtonClass(level, flow === level),
                )}
              >
                {FLOW_LABELS[level]}
              </button>
            ))}
          </div>
        </div>
        )}

        {/* Vitals (health sync + real camera measurement) */}
        {showVitals && (
        <div className="space-y-3">
          <span className="text-base font-semibold">Heart rate &amp; HRV</span>

          <HealthSyncCard
            onVitals={(hr, hrvValue) => {
              setHeartRate(String(hr));
              setHrv(String(hrvValue));
              setSyncedLabel('Synced just now');
            }}
          />

          <Button
            type="button"
            variant="outline"
            onClick={() => setScannerOpen(true)}
            className="h-12 w-full rounded-xl border-blush-strong/40 bg-blush/50 font-bold text-blush-foreground hover:bg-blush"
          >
            <Camera className="size-5" aria-hidden />
            Measure real pulse with camera
          </Button>

          <p className="text-xs leading-relaxed text-muted-foreground">
            {syncedLabel ? `${syncedLabel} · ` : ''}
            Camera scan measures your real pulse. {`Apple Health / Health Connect`} sync is
            simulated in this web version.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label htmlFor="heart-rate" className="text-sm font-medium text-muted-foreground">
                Resting HR (bpm)
              </label>
              <Input
                id="heart-rate"
                type="number"
                inputMode="numeric"
                min={30}
                max={220}
                placeholder="—"
                value={heartRate}
                onChange={(e) => setHeartRate(e.target.value)}
                className="h-14 rounded-xl text-lg font-semibold"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="hrv" className="text-sm font-medium text-muted-foreground">
                HRV (ms)
              </label>
              <Input
                id="hrv"
                type="number"
                inputMode="numeric"
                min={5}
                max={300}
                placeholder="—"
                value={hrv}
                onChange={(e) => setHrv(e.target.value)}
                className="h-14 rounded-xl text-lg font-semibold"
              />
            </div>
          </div>
        </div>
        )}

        {/* Symptoms */}
        <div className="space-y-3">
          <span className="text-base font-semibold">Symptoms</span>
          <div className="flex flex-wrap gap-2">
            {SYMPTOM_OPTIONS.map((symptom) => {
              const active = symptoms.has(symptom);
              return (
                <button
                  key={symptom}
                  type="button"
                  onClick={() => toggleSymptom(symptom)}
                  aria-pressed={active}
                  className={cn(
                    'rounded-full border-2 px-4 py-2.5 text-sm font-semibold transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    active
                      ? 'border-lavender-strong/60 bg-lavender text-lavender-foreground'
                      : 'border-border bg-white text-muted-foreground hover:bg-lavender/40',
                  )}
                >
                  {symptom}
                </button>
              );
            })}

            {/* Custom symptoms (removable) */}
            {customSymptoms.map((symptom) => {
              const active = symptoms.has(symptom);
              return (
                <span
                  key={symptom}
                  className={cn(
                    'flex items-center overflow-hidden rounded-full border-2 text-sm font-semibold transition-colors',
                    active
                      ? 'border-lavender-strong/60 bg-lavender text-lavender-foreground'
                      : 'border-border bg-white text-muted-foreground',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => toggleSymptom(symptom)}
                    aria-pressed={active}
                    className="py-2.5 pl-4 pr-2 focus-visible:outline-none"
                  >
                    {symptom}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onRemoveSymptom(symptom);
                      setSymptoms((prev) => {
                        const next = new Set(prev);
                        next.delete(symptom);
                        return next;
                      });
                    }}
                    aria-label={`Remove custom symptom ${symptom}`}
                    className="py-2.5 pl-1 pr-3 opacity-60 hover:opacity-100 focus-visible:outline-none"
                  >
                    <X className="size-3.5" aria-hidden />
                  </button>
                </span>
              );
            })}

            {/* Add symptom */}
            <button
              type="button"
              onClick={() => setAddingSymptom((v) => !v)}
              aria-expanded={addingSymptom}
              className={cn(
                'flex items-center gap-1.5 rounded-full border-2 border-dashed px-4 py-2.5 text-sm font-semibold transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                addingSymptom
                  ? 'border-aqua-strong/60 bg-aqua/60 text-aqua-foreground'
                  : 'border-border bg-white text-muted-foreground hover:bg-aqua/40',
              )}
            >
              <Plus className="size-4" aria-hidden />
              Add symptom
            </button>
          </div>

          {addingSymptom && (
            <div className="flex gap-2">
              <Input
                value={newSymptom}
                onChange={(e) => setNewSymptom(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submitNewSymptom();
                  }
                }}
                placeholder="e.g. Dizziness, Joint pain…"
                aria-label="New custom symptom"
                maxLength={30}
                autoFocus
                className="h-12 flex-1 rounded-xl text-base"
              />
              <Button
                type="button"
                onClick={submitNewSymptom}
                disabled={newSymptom.trim().length < 2}
                className="h-12 rounded-xl px-5 font-bold"
              >
                Add
              </Button>
            </div>
          )}
        </div>

        {/* Plans for this day */}
        {onSaveEvent && (
          <div className="space-y-2">
            <label htmlFor="day-plan" className="text-base font-semibold">
              Plans for this day <span className="text-sm font-normal text-muted-foreground">(optional)</span>
            </label>
            <Input
              id="day-plan"
              value={eventText}
              onChange={(e) => setEventText(e.target.value)}
              placeholder="e.g. Team meeting, dinner with friends…"
              maxLength={60}
              className="h-14 rounded-xl text-lg"
            />
            <p className="text-xs leading-relaxed text-muted-foreground">
              If high pain lands on a day with plans, Better Me will warn you and suggest
              rescheduling.
            </p>
          </div>
        )}

        <Button
          type="button"
          onClick={handleSave}
          className="h-14 w-full rounded-2xl text-lg font-bold shadow-sm"
        >
          <Check className="size-5" aria-hidden />
          {existing ? 'Update log' : 'Save log'}
        </Button>
      </CardContent>
    </Card>

    <PulseScannerDialog
      open={scannerOpen}
      onOpenChange={setScannerOpen}
      onResult={(bpm) => {
        setHeartRate(String(bpm));
        setSyncedLabel('Measured with camera just now');
      }}
    />
    </>
  );
}
